/**
 *
 * @Copyright 2024 UNLOCKIT DECENTRALIZATION, LDA
 * Development by VOID Software, SA
 *
 */

import { FunctionComponent, useState } from 'react';
import { Participant, Workflow, WorkflowParticipantRoleEnum } from '../../../types/workflows';
import { TranslationContext, withTranslationContext } from '../../controllers/TranslationContext';
import { ReactComponent as VerticalDotsIcon } from '../../../assets/images/vertical-dots.svg';
import { ReactComponent as ProfileIcon } from '../../../assets/images/profile.svg';
import Button from '../Button';
import { ManageParticipantModal } from './ManageParticipantModal';
import { useAuthContext } from '../../controllers/AuthenticationContext';
import { ShareComponent } from '../ShareComponent';
import { ReactComponent as SendIcon } from '../../../assets/images/plane.svg';
import { participantInvitationUrl } from '../../../services/workflows';

interface OwnProps extends TranslationContext {
    workflow: Workflow;
    participant: Participant;
    canTransferOwner: boolean;
    canManagePermissions: boolean;
    onRemoveParticipant: (participantId: number) => void;
    onUpdateParticipant: (updatedParticipant: Participant) => void;
    onTransferOwnership: (newOwnerId: string) => void;
}

const ParticipantCardComponent: FunctionComponent<OwnProps> = (props) => {
    const {
        t,
        workflow,
        participant,
        onRemoveParticipant,
        onUpdateParticipant,
        onTransferOwnership,
        canTransferOwner,
        canManagePermissions,
    } = props;

    const { user } = useAuthContext();

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const handleRemovedParticipant = () => {
        setIsModalOpen(false);

        onRemoveParticipant(participant.id);
    };

    const handleUpdateParticipantRole = (role: WorkflowParticipantRoleEnum) => {
        onUpdateParticipant({ ...participant, participantRole: role });
    };

    const renderManageParticipantButton = () => {
        if (canManagePermissions || participant.userId === user?.id) {
            return (
                <Button extraClasses="circular-ghost-btn" onClick={() => setIsModalOpen(true)} testId={`manage-participant-${participant.id}`}>
                    <VerticalDotsIcon />
                </Button>
            );
        }

        return null;
    };

    return (
        <>
            <div className="participant-card" data-testid={`participant-card-${participant.id}`}>
                <div className="participant-card__info">
                    <span className="participant-card__info__icon-wrap">
                        <ProfileIcon />
                    </span>
                    <div className="participant-card__info__details">
                        <p title={participant.name}>{participant.name}</p>
                        <p className="participant-card__info__details__role">{t(`participantRole.${participant.participantRole}`)}</p>
                    </div>
                </div>
                {canManagePermissions && (
                    <div className="participant-card__actions">
                        {participant.inviteToken && (
                            <ShareComponent
                                shareTitle={t('workflows.participants.participantActions.shareTitle')}
                                text={t('workflows.participants.participantActions.shareText', { workflow })}
                                url={participantInvitationUrl(participant.inviteToken)}
                                subject={t('workflows.participants.participantActions.emailSubject', { workflow })}
                                customIcon={<SendIcon />}
                                email={participant.inviteEmail}
                                copyButtonText={t('workflows.participants.participantActions.copyInvite')}
                            />
                        )}
                        <div className="participant-card__actions__icon-wrap">
                            {renderManageParticipantButton()}
                        </div>
                    </div>
                )}
            </div>
            <ManageParticipantModal
                open={isModalOpen}
                onModalClose={() => setIsModalOpen(false)}
                canTransferOwner={canTransferOwner}
                canManagePermissions={canManagePermissions}
                participant={participant}
                onRemoveParticipant={handleRemovedParticipant}
                onUpdateParticipantRole={handleUpdateParticipantRole}
                onTransferOwnership={onTransferOwnership}
                workflowType={workflow.transactionType}
            />
        </>
    );
};

export const ParticipantCard = withTranslationContext(ParticipantCardComponent);
